import React, { useCallback, useEffect, useState } from 'react';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '../ui/sheet';
import { Badge } from '../ui/badge';
import {
  Activity, UserPlus, Mail, KeyRound, Building2, Trash2, ShieldAlert,
  ChevronRight, Clock,
} from 'lucide-react';
import api from '../../lib/api';
import logger from '../../lib/logger';

/**
 * AdminActivityTimelineOverlay
 * ----------------------------
 * Right-side slide-over rendering the last 24h of admin-relevant
 * audit events as a clickable timeline. Each row deep-links into
 * the affected surface (Users tab + pulse the user row, or
 * Organizations tab + pulse the org card) so admins can replay
 * "what just happened" without scrubbing the full audit log.
 *
 * Pulses reuse the same `pulsingUserId` / `pulsingOrgId` machinery
 * already wired in AdminDashboardPage, so this component is a
 * pure-presentation layer: its only job is to surface clickable
 * navigation intents back to the parent.
 */

// Same icon/tone palette as RecentAdminActivityStrip so the two
// surfaces feel visually consistent.
const ACTION_PRESENTATION = {
  'user.created':   { icon: UserPlus,    label: 'created user',    tone: 'text-emerald-700 bg-emerald-50' },
  'user.invited':   { icon: Mail,        label: 'invited user',    tone: 'text-teal-700 bg-teal-50' },
  'user.updated':   { icon: ShieldAlert, label: 'updated user',    tone: 'text-slate-700 bg-slate-100' },
  'user.deleted':   { icon: Trash2,      label: 'deleted user',    tone: 'text-rose-700 bg-rose-50' },
  'role.assigned':  { icon: KeyRound,    label: 'assigned role',   tone: 'text-amber-700 bg-amber-50' },
  'role.revoked':   { icon: KeyRound,    label: 'revoked role',    tone: 'text-rose-700 bg-rose-50' },
  'org.created':    { icon: Building2,   label: 'created org',     tone: 'text-indigo-700 bg-indigo-50' },
  'org.updated':    { icon: Building2,   label: 'updated org',     tone: 'text-slate-700 bg-slate-100' },
  'org.deleted':    { icon: Building2,   label: 'deleted org',     tone: 'text-rose-700 bg-rose-50' },
};

function presentationFor(action) {
  return ACTION_PRESENTATION[action] || {
    icon: Activity, label: action, tone: 'text-slate-700 bg-slate-100',
  };
}

function relativeTime(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const seconds = Math.max(1, Math.round((Date.now() - then) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
}

function describe(row) {
  return row.target_name || row.target_email || row.target_id || row.metadata?.target_email || '';
}

/**
 * Bucket events by day so the scrub-style timeline groups
 * "today" / "yesterday" entries cleanly.
 */
function bucketByDay(rows) {
  const buckets = new Map();
  rows.forEach((r) => {
    const ts = r.created_at || r.timestamp;
    const d = ts ? new Date(ts) : null;
    const key = d ? d.toISOString().slice(0, 10) : 'unknown';
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key).push(r);
  });
  return Array.from(buckets.entries()).sort((a, b) => b[0].localeCompare(a[0]));
}

function bucketLabel(key) {
  if (key === 'unknown') return 'Unknown';
  const today = new Date().toISOString().slice(0, 10);
  const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
  if (key === today) return 'Today';
  if (key === yesterday) return 'Yesterday';
  return new Date(key).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}

/**
 * Map an audit row to a navigation intent the parent can act on.
 * Returns null when the event has no actionable target on the
 * Admin Dashboard (e.g. a deleted org no longer exists, so there's
 * nothing to pulse).
 */
function navIntentFor(row) {
  const action = row.action || '';
  const targetId = row.target_id;
  if (!targetId) return null;
  if (action.startsWith('user.') && action !== 'user.deleted') {
    return { surface: 'user', id: targetId };
  }
  if (action.startsWith('role.') && row.target_type === 'user') {
    return { surface: 'user', id: targetId };
  }
  if (action.startsWith('org.') && action !== 'org.deleted') {
    return { surface: 'org', id: targetId };
  }
  return null;
}

export default function AdminActivityTimelineOverlay({
  open,
  onClose,
  onNavigate,
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Actor filter — narrows the timeline to a single admin's actions
  // so multi-admin shops can answer "what did Sarah just do?" in one
  // scan. 'all' is the default; otherwise we match on actor_id.
  const [actorFilter, setActorFilter] = useState('all');

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const startDate = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
      // Pull each admin-relevant category in parallel — same pattern
      // as RecentAdminActivityStrip but with a higher per-category
      // limit (50 each = 150 max) and a 24h date floor so we get
      // a representative scrub buffer without flooding the sheet.
      const [usersRes, rolesRes, orgsRes] = await Promise.all([
        api.get('/rbac/audit-logs', { params: { category: 'users', limit: 50, start_date: startDate } }),
        api.get('/rbac/audit-logs', { params: { category: 'roles', limit: 50, start_date: startDate } }),
        api.get('/rbac/audit-logs', { params: { category: 'orgs',  limit: 50, start_date: startDate } }),
      ]);
      const merged = [
        ...(usersRes.data?.logs || []),
        ...(rolesRes.data?.logs || []),
        ...(orgsRes.data?.logs  || []),
      ];
      merged.sort((a, b) => {
        const ta = new Date(a.created_at || a.timestamp).getTime();
        const tb = new Date(b.created_at || b.timestamp).getTime();
        return tb - ta;
      });
      setRows(merged);
    } catch (err) {
      logger.error('AdminActivityTimelineOverlay fetch failed', err);
      if (err?.response?.status === 403) {
        setRows([]);
      } else {
        setError('Could not load activity timeline.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) refresh();
  }, [open, refresh]);

  const buckets = bucketByDay(
    actorFilter === 'all' ? rows : rows.filter((r) => r.actor_id === actorFilter),
  );

  // Build the actor option list from the (unfiltered) rows so the
  // dropdown stays stable even after the user picks a single actor.
  const actorOptions = React.useMemo(() => {
    const map = new Map();
    rows.forEach((r) => {
      if (r.actor_id && !map.has(r.actor_id)) {
        map.set(r.actor_id, r.actor_name || r.actor_email || r.actor_id);
      }
    });
    return Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [rows]);

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <SheetContent
        side="right"
        className="w-[480px] sm:max-w-[520px] overflow-y-auto"
        data-testid="admin-activity-timeline-overlay"
      >
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-teal-600" />
            Activity timeline
          </SheetTitle>
          <SheetDescription>
            Last 24 hours of admin actions. Click any event to jump back to the
            affected user or organization — the row will pulse so you can find
            it instantly.
          </SheetDescription>
        </SheetHeader>

        {actorOptions.length > 1 && (
          <div className="mt-4 flex items-center gap-2">
            <label className="text-xs font-medium text-slate-600 shrink-0">
              Filter by actor:
            </label>
            <select
              value={actorFilter}
              onChange={(e) => setActorFilter(e.target.value)}
              className="flex-1 rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm text-slate-700 focus:border-teal-400 focus:outline-none"
              data-testid="timeline-actor-filter"
            >
              <option value="all">All admins ({rows.length})</option>
              {actorOptions.map(([id, label]) => {
                const count = rows.filter((r) => r.actor_id === id).length;
                return (
                  <option key={id} value={id}>{label} ({count})</option>
                );
              })}
            </select>
            {actorFilter !== 'all' && (
              <button
                type="button"
                onClick={() => setActorFilter('all')}
                className="text-xs text-slate-500 hover:text-slate-700"
                data-testid="timeline-actor-filter-clear"
              >
                Clear
              </button>
            )}
          </div>
        )}

        <div className="mt-6 space-y-6">
          {loading && (
            <p className="text-sm text-slate-500" data-testid="timeline-loading">
              Loading the last 24 hours…
            </p>
          )}
          {error && (
            <p className="text-sm text-rose-600" data-testid="timeline-error">
              {error}
            </p>
          )}
          {!loading && !error && rows.length === 0 && (
            <p className="text-sm text-slate-500" data-testid="timeline-empty">
              No admin activity in the last 24 hours.
            </p>
          )}

          {buckets.map(([day, items]) => (
            <div key={day} data-testid={`timeline-bucket-${day}`}>
              <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                <span>{bucketLabel(day)}</span>
                <span className="h-px flex-1 bg-slate-200" />
                <Badge variant="outline" className="text-[10px] font-normal">
                  {items.length} event{items.length !== 1 ? 's' : ''}
                </Badge>
              </div>
              <ol className="relative border-l border-slate-200 pl-4 space-y-2">
                {items.map((row, idx) => {
                  const { icon: Icon, label, tone } = presentationFor(row.action);
                  const subject = describe(row);
                  const intent = navIntentFor(row);
                  const clickable = !!intent;
                  return (
                    <li
                      key={row.id || `${row.action}-${row.created_at || row.timestamp}-${idx}`}
                      className="relative"
                      data-testid={`timeline-row-${idx}`}
                    >
                      <span className={`absolute -left-[22px] top-1 flex h-5 w-5 items-center justify-center rounded-full ${tone} ring-2 ring-white`}>
                        <Icon size={11} />
                      </span>
                      <button
                        type="button"
                        disabled={!clickable}
                        onClick={() => clickable && onNavigate?.(intent, row)}
                        className={
                          'w-full rounded-md border border-transparent px-3 py-2 text-left text-sm transition '
                          + (clickable
                            ? 'hover:border-amber-200 hover:bg-amber-50/60 cursor-pointer'
                            : 'cursor-default')
                        }
                        data-testid={`timeline-row-btn-${idx}`}
                        data-clickable={clickable ? 'true' : 'false'}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <p className="min-w-0 truncate text-slate-800">
                            <span className="font-medium">
                              {row.actor_name || row.actor_email || 'Unknown admin'}
                            </span>
                            <span className="text-slate-500"> · {label}</span>
                            {subject && (
                              <span className="text-slate-700"> · <span className="font-mono text-[12.5px]">{subject}</span></span>
                            )}
                          </p>
                          {clickable && (
                            <ChevronRight size={14} className="shrink-0 text-slate-400" />
                          )}
                        </div>
                        <div className="mt-0.5 flex items-center gap-2 text-xs text-slate-400">
                          <span title={row.created_at || row.timestamp}>
                            {relativeTime(row.created_at || row.timestamp)}
                          </span>
                          {row.action && (
                            <code className="rounded bg-slate-100 px-1 py-0.5 text-[10px] font-mono text-slate-500">
                              {row.action}
                            </code>
                          )}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ol>
            </div>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}
